import { Editor } from "draft-js";
import { withNamespaces } from "react-i18next";
import { MdOutlineModeEdit } from "react-icons/md";
import { Button, Col, FormGroup, Label, Modal, ModalBody, ModalHeader, Row, Spinner } from "reactstrap";
import { INTERNATIONAL_DATE_FORMAT } from "src/common/constants";
import { isFeatureEnabledForActiveModule } from "src/helpers/module_helper";
import LocalizedPhoneInput from "src/modules/3rd-party-management/components/LocalizedPhoneInput";
import { getThirdPartyCategoryLabel } from "src/modules/3rd-party-management/helpers/thirdPartyCategory";
import DateUtils from "src/services/utils/DateUtils";
import UserUtils from "src/services/utils/UserUtils";

const ThirdParty = ({
    t,
    goToSpecificTab,
    goToPrevTab,
    onSubmit,
    isLoading,
    thirdPartyType,
    values
}) => {
    const dateUtils = new DateUtils();

    const handleBack = () => {
        goToPrevTab();
    };

    const {
        companyDetails,
        contacts,
        factory
    } = values;

    const factoryAddresses = (factory.factory_addresses || []);
    const officeAddresses = (factory.office_addresses || []);
    const products = (factory.products || []).filter((product) => !!product.product);

    const contactsToDisplay = contacts?.filter((contact) => !contact.isTemp);

    const thirdpartyCategoryLabel = getThirdPartyCategoryLabel(companyDetails.subType, thirdPartyType, t, companyDetails.customSubType);

    const renderIndividualTypeBody = () => {
        return (
            <div className="confirm-form-sections-container">
                {/* Company Detials */}
                <div className="section">
                    <div className="section-header"
                        style={{
                            display: "flex",
                            flexFlow: "row wrap",
                            justifyContent: "space-between",
                        }}
                    >
                        <span>
                            {`1. ${t("Personal Details")}`}
                        </span>

                        <Button color="primary" onClick={() => goToSpecificTab(1)} outline className="edit-section-btn">
                            <MdOutlineModeEdit />
                        </Button>
                    </div>
                    <div className="section-body mt-0">
                        <Row>
                            <Col lg="3" md="6" sm="12">
                                <Label for="companyName">
                                    {t('Third Party Category')}:
                                </Label>
                                <p>
                                    {thirdpartyCategoryLabel}
                                </p>
                            </Col>

                            <Col lg="3" md="6" sm="12">
                                <Label for="companyName">
                                    {t("Name")}:
                                </Label>
                                <p>{companyDetails.company_name}</p>
                            </Col>

                            <Col lg="3" md="6" sm="12">
                                <Label for="companyShortname">
                                    {t("Nickname / AKA")}:
                                </Label>
                                <p>{companyDetails.company_shortname ? companyDetails.company_shortname : '--'}</p>
                            </Col>

                            <Col lg="3" md="6" sm="12">
                                <Label for="individual_date_of_birth">{t("Date of Birth")}: </Label>
                                <p>
                                    {companyDetails.individual_date_of_birth ? dateUtils.convertDateToDate(
                                        companyDetails.individual_date_of_birth, 
                                        INTERNATIONAL_DATE_FORMAT
                                    ) : '--'}
                                </p>
                            </Col>

                            <Col lg="3" md="6" sm="12">
                                <Label for="individual_birth_place_country">{t("Country of Birth")}: </Label>
                                <p>
                                    {companyDetails.individual_birth_place_country ? t(companyDetails.individual_birth_place_country.label) : '--'}
                                </p>
                            </Col>

                            <Col lg="3" md="6" sm="12">
                                <Label for="individual_tax_id">{t("Tax ID")}: </Label>
                                <p>
                                    {companyDetails.individual_tax_id ? companyDetails.individual_tax_id : '--'}
                                </p>
                            </Col>

                            <Col lg="3" md="6" sm="12">
                                <Label for="individual_national_id_number">{t("National ID Number")}: </Label>
                                <p>
                                    {companyDetails.individual_national_id_number ? companyDetails.individual_national_id_number : "--"}
                                </p>
                            </Col>

                            {isFeatureEnabledForActiveModule('entity_management') && (
                                <Col sm="12">
                                    <Row>
                                        <Col lg="6" md="12">
                                            <Label>{t("Connected Legal Entities")}: </Label>
                                            <p>{(companyDetails.entities || []).map((legal) => legal.label).join(', ')}</p>
                                        </Col>
                                    </Row>
                                </Col>
                            )}

                            <Col sm="12">
                                <Label for="description">
                                    {t("Description")}:
                                </Label>
                                <Editor
                                    editorState={companyDetails.description}
                                    readOnly={true}
                                    toolbarClassName="toolbarClassName"
                                    wrapperClassName="wrapperClassName"
                                    editorClassName="editorClassName"
                                />
                            </Col>
                        </Row>
                    </div>
                </div>

                {/* Factory Sites and Products */}
                <div className="section">
                    <div
                        className="section-header"
                        style={{
                            display: "flex",
                            flexFlow: "row wrap",
                            justifyContent: "space-between",
                        }}
                    >
                        <span>{`2. ${t("Location and Products")}`}</span>
                        <Button color="primary" onClick={() => goToSpecificTab(2)} outline className="edit-section-btn">
                            <MdOutlineModeEdit />
                        </Button>
                    </div>

                    <div className="section-body mt-0">
                        {/* Office Address */}
                        <label>
                            {t("Residential Address")}
                        </label>
                        <ol>
                            {officeAddresses.map((address, i) => {
                                return (
                                    <li key={i}>
                                        <Row className="mb-2">
                                            <Col md="3" sm="12">
                                                <Label for="country">{t("Country")}: </Label>
                                                <p>{t(address?.country?.label)}</p>
                                            </Col>

                                            <Col md="2" sm="12">
                                                <Label for="postal-code">{t("Postal Code")}: </Label>
                                                <p>{t(address?.postalCode)}</p>
                                            </Col>

                                            <Col md="2" sm="12">
                                                <Label for="city">{t("City")}: </Label>
                                                <p>{t(address?.city)}</p>
                                            </Col>

                                            <Col md="5" sm="12">
                                                <Label for="extra">{t("Street, Number")}: </Label>
                                                <p>{t(address?.extra)}</p>
                                            </Col>
                                        </Row>
                                    </li>
                                );
                            })}
                        </ol>

                        {products?.length > 0 ? (
                            <>
                                {/* Products */}
                                <div>
                                    <label>{t("List of products supplied")}</label>
                                    <Row className="mb-2">
                                        <Col sm='12'>
                                            <div className="table-container">
                                                <table>
                                                    <thead>
                                                        <tr>
                                                            <th style={{ width: "25%" }}>{t("Product Name")}</th>
                                                            <th style={{ width: "75%" }}>{t("Description")}</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {products.map((product, index) => {
                                                            return (
                                                                <tr key={index}>
                                                                    <td style={{ width: "25%" }}>
                                                                        <p>
                                                                            {product.product.label}
                                                                        </p>
                                                                    </td>
                                                                    <td style={{ width: "75%" }}>
                                                                        <p>
                                                                            {product.description}
                                                                        </p>
                                                                    </td>
                                                                </tr>
                                                            )
                                                        })}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </Col>
                                    </Row>
                                </div>
                            </>
                        ) : null}

                    </div>
                </div>

                {contactsToDisplay?.length > 0 ? (
                    <>
                        {/* Contact Person */}
                        <div className="section">
                            <div
                                className="section-header"
                                style={{
                                    display: "flex",
                                    flexFlow: "row wrap",
                                    justifyContent: "space-between",
                                }}
                            >
                                <span>{`3. ${t("Contact Person")}`}</span>
                                <Button color="primary" onClick={() => goToSpecificTab(3)} outline className="edit-section-btn">
                                    <MdOutlineModeEdit />
                                </Button>
                            </div>

                            <div className="section-body mt-0">
                                <ol>
                                    {contactsToDisplay.map((item, i) => {
                                        return (
                                            <li key={i}>
                                                <Row>
                                                    <Col md="4" sm="12">
                                                        <Label for="fullName">{t("Full Name")}: </Label>
                                                        <p>{`(${t(item.title)}) ${item.firstName} ${item.lastName}`}</p>
                                                    </Col>

                                                    <Col md="4" sm="12">
                                                        <Label for="position">{t("Position")}: </Label>
                                                        <p>{t(item.position)}</p>
                                                    </Col>

                                                    <Col md="4" sm="12">
                                                        <Label for="language">{t("Language")}: </Label>
                                                        <p>{t(item.language?.label)}</p>
                                                    </Col>
                                                </Row>

                                                <Row>
                                                    <Col md="4" sm="12">
                                                        <Label for="email">{t("Email")}: </Label>
                                                        <p>{item.email}</p>
                                                    </Col>

                                                    <Col md="4" sm="12">
                                                        <Label for="phone">{t("Phone Number")}: </Label>
                                                        <LocalizedPhoneInput
                                                            disabled={true}
                                                            buttonStyle={{
                                                                display: 'none'
                                                            }}
                                                            dropdownStyle={{
                                                                border: 'none'
                                                            }}
                                                            inputStyle={{
                                                                border: 'none',
                                                                paddingLeft: '0px',
                                                                color: '#000',
                                                                fontSize: '14px',
                                                                background: 'transparent'
                                                            }}
                                                            disableDropdown={true}
                                                            value={item.phoneNumber}
                                                        />
                                                    </Col>
                                                </Row>
                                            </li>
                                        );
                                    })}
                                </ol>
                            </div>
                        </div>
                    </>
                ) : null}
            </div>
        )
    }

    const renderCompanyTypeBody = () => {
        return (
            <div className="confirm-form-sections-container">
                {/* Company Detials */}
                <div className="section">
                    <div className="section-header"
                        style={{
                            display: "flex",
                            flexFlow: "row wrap",
                            justifyContent: "space-between",
                        }}
                    >
                        <span>
                            {`1. ${t("Company Details")}`}
                        </span>

                        <Button color="primary" onClick={() => goToSpecificTab(1)} outline className="edit-section-btn">
                            <MdOutlineModeEdit />
                        </Button>
                    </div>
                    <div className="section-body mt-0">
                        <Row>
                            <Col lg="3" md="6" sm="12">
                                <Label for="companyName">
                                    {t('Third Party Category')}:
                                </Label>
                                <p>
                                    {thirdpartyCategoryLabel}
                                </p>
                            </Col>

                            <Col lg="3" md="6" sm="12">
                                <Label for="companyName">
                                    {t("Company Name")}:
                                </Label>
                                <p>{companyDetails.company_name}</p>
                            </Col>

                            <Col lg="3" md="6" sm="12">
                                <Label for="companyShortname">
                                    {t("Short Name")}:
                                </Label>
                                <p>{companyDetails.company_shortname}</p>
                            </Col>

                            <Col lg="3" md="6" sm="12">
                                <Label for="industry">{t("Industries")}: </Label>
                                <p style={{ whiteSpace: 'pre-line' }}>
                                    {(companyDetails.industries || []).map((industry) => industry.label).join('\n')}
                                </p>
                            </Col>
                        </Row>

                        <Row>
                            <Col lg="3" md="6" sm="12">
                                <Label for="vatNumber">{t("VAT ID")}: </Label>
                                <p>{t(companyDetails.vat_number)}</p>
                            </Col>

                            <Col lg="3" md="6" sm="12">
                                <Label for="companySize">{t("Company Size")}: </Label>
                                <p>{t(companyDetails.company_size)}</p>
                            </Col>

                            <Col lg="3" md="6" sm="12">
                                <Label for="brands">{t("Brands")}: </Label>
                                <p>{t(companyDetails.brands)}</p>
                            </Col>

                            <Col lg="3" md="6" sm="12">
                                <Label for="website">{t("Website")}: </Label>
                                <p>{companyDetails.website}</p>
                            </Col>

                            {isFeatureEnabledForActiveModule('entity_management') && (
                                <Col lg="6" md="12" sm="12">
                                    <Label>{t("Connected Legal Entities")}: </Label>
                                    <p>{(companyDetails.entities || []).map((legal) => legal.label).join(', ')}</p>
                                </Col>
                            )}
                        </Row>

                        <Row>
                            <Col>
                                <Label for="description">
                                    {t("Description")}:
                                </Label>
                                <Editor
                                    editorState={companyDetails.description}
                                    readOnly={true}
                                    toolbarClassName="toolbarClassName"
                                    wrapperClassName="wrapperClassName"
                                    editorClassName="editorClassName"
                                />
                            </Col>
                        </Row>
                    </div>
                </div>

                {/* Factory Sites and Products */}
                <div className="section">
                    <div
                        className="section-header"
                        style={{
                            display: "flex",
                            flexFlow: "row wrap",
                            justifyContent: "space-between",
                        }}
                    >
                        <span>{`2. ${t("Location and Products")}`}</span>
                        <Button color="primary" onClick={() => goToSpecificTab(2)} outline className="edit-section-btn">
                            <MdOutlineModeEdit />
                        </Button>
                    </div>

                    <div className="section-body mt-0">
                        {/* Office Address */}
                        <label>
                            {t("Office Address")}
                        </label>
                        <ol>
                            {officeAddresses.map((address, i) => {
                                return (
                                    <li key={i}>
                                        <Row className="mb-2">
                                            <Col md="3" sm="12">
                                                <Label for="country">{t("Country")}: </Label>
                                                <p>{t(address?.country?.label)}</p>
                                            </Col>

                                            <Col md="2" sm="12">
                                                <Label for="postal-code">{t("Postal Code")}: </Label>
                                                <p>{t(address?.postalCode)}</p>
                                            </Col>

                                            <Col md="2" sm="12">
                                                <Label for="city">{t("City")}: </Label>
                                                <p>{t(address?.city)}</p>
                                            </Col>

                                            <Col md="5" sm="12">
                                                <Label for="extra">{t("Street, Number")}: </Label>
                                                <p>{t(address?.extra)}</p>
                                            </Col>
                                        </Row>
                                    </li>
                                );
                            })}
                        </ol>

                        {factoryAddresses?.length > 0 ? (
                            <>
                                {/* Factory Address */}
                                <label>{t("Location")}</label>
                                <ol>
                                    {factoryAddresses.map((address, i) => {
                                        return (
                                            <li key={i}>
                                                <Row className="mb-2">
                                                    <Col md="3" sm="12">
                                                        <Label for="country">{t("Country")}: </Label>
                                                        <p>{t(address.country?.label)}</p>
                                                    </Col>

                                                    <Col md="2" sm="12">
                                                        <Label for="postal-code">{t("Postal Code")}: </Label>
                                                        <p>{t(address.postalCode)}</p>
                                                    </Col>

                                                    <Col md="2" sm="12">
                                                        <Label for="city">{t("City")}: </Label>
                                                        <p>{t(address.city)}</p>
                                                    </Col>

                                                    <Col md="5" sm="12">
                                                        <Label for="extra">{t("Street, Number")}: </Label>
                                                        <p>{t(address.extra)}</p>
                                                    </Col>
                                                </Row>
                                            </li>
                                        )
                                    })}
                                </ol>
                            </>
                        ) : null}

                        {products?.length > 0 ? (
                            <>
                                {/* Products */}
                                <div>
                                    <label>{t("List of products supplied")}</label>
                                    <Row className="mb-2">
                                        <Col sm='12'>
                                            <div className="table-container">
                                                <table>
                                                    <thead>
                                                        <tr>
                                                            <th style={{ width: "25%" }}>{t("Product Name")}</th>
                                                            <th style={{ width: "75%" }}>{t("Description")}</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {products.map((product, index) => {
                                                            return (
                                                                <tr key={index}>
                                                                    <td style={{ width: "25%" }}>
                                                                        <p>
                                                                            {product.product.label}
                                                                        </p>
                                                                    </td>
                                                                    <td style={{ width: "75%" }}>
                                                                        <p>
                                                                            {product.description}
                                                                        </p>
                                                                    </td>
                                                                </tr>
                                                            )
                                                        })}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </Col>
                                    </Row>
                                </div>
                            </>
                        ) : null}

                    </div>
                </div>

                {contactsToDisplay?.length > 0 ? (
                    <>
                        {/* Contact Person */}
                        <div className="section">
                            <div
                                className="section-header"
                                style={{
                                    display: "flex",
                                    flexFlow: "row wrap",
                                    justifyContent: "space-between",
                                }}
                            >
                                <span>{`3. ${t("Contact Person")}`}</span>
                                <Button color="primary" onClick={() => goToSpecificTab(3)} outline className="edit-section-btn">
                                    <MdOutlineModeEdit />
                                </Button>
                            </div>

                            <div className="section-body mt-0">
                                <ol>
                                    {contactsToDisplay.map((item, i) => {
                                        return (
                                            <li key={i}>
                                                <Row>
                                                    <Col md="4" sm="12">
                                                        <Label for="fullName">{t("Full Name")}: </Label>
                                                        <p>{`(${t(item.title)}) ${item.firstName} ${item.lastName}`}</p>
                                                    </Col>

                                                    <Col md="4" sm="12">
                                                        <Label for="position">{t("Position")}: </Label>
                                                        <p>{t(item.position)}</p>
                                                    </Col>

                                                    <Col md="4" sm="12">
                                                        <Label for="language">{t("Language")}: </Label>
                                                        <p>{t(item.language?.label)}</p>
                                                    </Col>
                                                </Row>

                                                <Row>
                                                    <Col md="4" sm="12">
                                                        <Label for="email">{t("Email")}: </Label>
                                                        <p>{item.email}</p>
                                                    </Col>

                                                    <Col md="4" sm="12">
                                                        <Label for="phone">{t("Phone Number")}: </Label>
                                                        <LocalizedPhoneInput
                                                            disabled={true}
                                                            buttonStyle={{
                                                                display: 'none'
                                                            }}
                                                            dropdownStyle={{
                                                                border: 'none'
                                                            }}
                                                            inputStyle={{
                                                                border: 'none',
                                                                paddingLeft: '0px',
                                                                color: '#000',
                                                                fontSize: '14px',
                                                                background: 'transparent'
                                                            }}
                                                            disableDropdown={true}
                                                            value={item.phoneNumber}
                                                        />
                                                    </Col>
                                                </Row>
                                            </li>
                                        );
                                    })}
                                </ol>
                            </div>
                        </div>
                    </>
                ) : null}
            </div>
        )
    }

    return (
        <>
            {thirdPartyType === 'individual' ? (
                renderIndividualTypeBody()
            ) : (
                renderCompanyTypeBody()
            )}

            <Row className="mb-4">
                <FormGroup
                    style={{
                        display: "flex",
                        flexFlow: "row wrap",
                        justifyContent: "space-between",
                    }}
                    className="actions-buttons-container"
                >
                    <Button color="primary" disabled={isLoading} onClick={handleBack} outline>
                        {t("Back")}
                    </Button>
                    <Button color="primary" disabled={isLoading} onClick={onSubmit}>
                        {isLoading ? (
                            <>
                                {t("Creating...")}
                            </>
                        ) : (
                            <>
                                {t("Create")}
                            </>
                        )}
                    </Button>
                </FormGroup>
            </Row>

            <Modal isOpen={isLoading}>
                <ModalHeader>
                    <div>
                        <Spinner className="me-2" animation="border" variant="info" size="sm"/>
                        {t('Creating new third party')}
                    </div>
                </ModalHeader>
                
                <ModalBody>
                    <p>
                        {t("This might take several minutes. please don't close or refresh the page")}
                    </p>
                </ModalBody>
            </Modal>
        </>
    )
}

const OwnCompany = ({
    t,
    goToSpecificTab,
    goToPrevTab,
    onSubmit,
    isLoading,
    values
}) => {
    const userUtils = new UserUtils();

    const handleBack = () => {
        goToPrevTab();
    };

    const {
        companyDetails,
        contacts,
        factory
    } = values;

    const factoryAddresses = (factory.factory_addresses || []);
    const officeAddresses = (factory.office_addresses || []);
    const products = (factory.products || []).filter((product) => !!product.product);

    return (
        <>
            <div className="confirm-form-sections-container">
                <div className="section">
                    <div
                        className="section-header"
                        style={{
                            display: "flex",
                            flexFlow: "row wrap",
                            justifyContent: "space-between",
                        }}
                    >
                        <span>{`1. ${t("Company Details")}`}</span>
                        <Button color="primary" onClick={() => goToSpecificTab(1)} outline className="edit-section-btn">
                            <MdOutlineModeEdit />
                        </Button>
                    </div>
                    <div className="section-body mt-0">
                        <Row>
                            <Col lg="3" md="6" sm="12">
                                <Label>{t("Connected Legal Entity")}: </Label>
                                <p>{companyDetails.company_name}</p>
                            </Col>

                            <Col lg="3" md="6" sm="12">
                                <Label>{t("Short Name")}: </Label>
                                <p>{companyDetails.company_short_name}</p>
                            </Col>

                            <Col lg="3" md="6" sm="12">
                                <Label for="brands">{t("Brands")}: </Label>
                                <p>{companyDetails.brands}</p>
                            </Col>

                            <Col lg="3" md="6" sm="12">
                                <Label for="vatNumber">{t("VAT ID")}: </Label>
                                <p>{companyDetails.vat_number}</p>
                            </Col>
                        </Row>

                        <Row>
                            <Col lg="3" md="6" sm="12">
                                <Label for="industry">{t("Industries")}: </Label>
                                <p style={{ whiteSpace: 'pre-line' }}>
                                    {(companyDetails.industries || []).map((industry) => industry.label).join('\n')}
                                </p>
                            </Col>

                            <Col lg="3" md="6" sm="12">
                                <Label for="companySize">{t("Company Size")}: </Label>
                                <p>{t(companyDetails.company_size)}</p>
                            </Col>

                            <Col lg="6" md="6" sm="12">
                                <Label for="website">{t("Website")}: </Label>
                                <p>{companyDetails.website}</p>
                            </Col>
                        </Row>

                        <Row>
                            <Col>
                                <Label for="description">{t("Description")}: </Label>
                                <Editor
                                    editorState={companyDetails.description}
                                    readOnly={true}
                                    toolbarClassName="toolbarClassName"
                                    wrapperClassName="wrapperClassName"
                                    editorClassName="editorClassName"
                                />
                            </Col>
                        </Row>
                    </div>
                </div>

                <div className="section">
                    <div
                        className="section-header"
                        style={{
                            display: "flex",
                            flexFlow: "row wrap",
                            justifyContent: "space-between",
                        }}
                    >
                        <span>{`2. ${t("Location and Products")}`}</span>
                        <Button color="primary" onClick={() => goToSpecificTab(2)} outline className="edit-section-btn">
                            <MdOutlineModeEdit />
                        </Button>
                    </div>
                    <div className="section-body mt-0">
                        <label>{t("Office Address")}</label>
                        <ol>
                            {officeAddresses.map((address, i) => {
                                return (
                                    <li key={i}>
                                        <Row className="mb-2">
                                            <Col md="3" sm="12">
                                                <Label for="country">{t("Country")}: </Label>
                                                <p>{t(address.country?.label)}</p>
                                            </Col>

                                            <Col md="2" sm="12">
                                                <Label for="postal-code">{t("Postal Code")}: </Label>
                                                <p>{address.postalCode}</p>
                                            </Col>

                                            <Col md="2" sm="12">
                                                <Label for="city">{t("City")}: </Label>
                                                <p>{t(address.city)}</p>
                                            </Col>

                                            <Col md="5" sm="12">
                                                <Label for="extra">{t("Street, Number")}: </Label>
                                                <p>{address.extra}</p>
                                            </Col>
                                        </Row>
                                    </li>
                                );
                            })}
                        </ol>

                        {factoryAddresses.length > 0 ? (
                            <>
                                <label>{t("Location")}</label>
                                <ol>
                                    {factoryAddresses.map((address, i) => {
                                        return (
                                            <li key={i}>
                                                <Row className="mb-2">
                                                    <Col md="3" sm="12">
                                                        <Label for="country">{t("Country")}: </Label>
                                                        <p>{t(address.country?.label)}</p>
                                                    </Col>

                                                    <Col md="2" sm="12">
                                                        <Label for="postal-code">{t("Postal Code")}: </Label>
                                                        <p>{address.postalCode}</p>
                                                    </Col>

                                                    <Col md="2" sm="12">
                                                        <Label for="city">{t("City")}: </Label>
                                                        <p>{address.city}</p>
                                                    </Col>

                                                    <Col md="5" sm="12">
                                                        <Label for="extra">{t("Street, Number")}: </Label>
                                                        <p>{address.extra}</p>
                                                    </Col>
                                                </Row>
                                            </li>
                                        )
                                    })}
                                </ol>
                            </>
                        ) : null}

                        {products?.length > 0 ? (
                            <div>
                                <label>{t("List of products supplied")}</label>
                                <Row className="mb-2">
                                    <Col sm='12'>
                                        <div className="table-container">
                                            <table>
                                                <thead>
                                                    <tr>
                                                        <th style={{ width: "25%" }}>{t("Product Name")}</th>
                                                        <th style={{ width: "75%" }}>{t("Description")}</th>
                                                    </tr>
                                                </thead>

                                                <tbody>
                                                    {products.map((product, index) => {
                                                        return (
                                                            <tr key={index}>
                                                                <td style={{ width: "25%" }}>
                                                                    <p>
                                                                        {product.product.label}
                                                                    </p>
                                                                </td>
                                                                <td style={{ width: "75%" }}>
                                                                    <p>
                                                                        {product.description}
                                                                    </p>
                                                                </td>
                                                            </tr>
                                                        );
                                                    })}
                                                </tbody>
                                            </table>
                                        </div>
                                    </Col>
                                </Row>
                            </div>
                        ) : null}
                    </div>
                </div>

                {contacts.length > 0 ? (
                    <div className="section">
                        <div
                            className="section-header"
                            style={{
                                display: "flex",
                                flexFlow: "row wrap",
                                justifyContent: "space-between",
                            }}
                        >
                            <span>{`3. ${t("Responsible Person")}`}</span>
                            <Button color="primary" onClick={() => goToSpecificTab(3)} outline className="edit-section-btn">
                                <MdOutlineModeEdit />
                            </Button>
                        </div>

                        <div className="section-body mt-0">
                            <ol>
                                {contacts.map((item, i) => {
                                    return (
                                        <li key={i}>
                                            <Row>
                                                <Col md="4" sm="12">
                                                    <Label>{t("Full Name")}: </Label>
                                                    <p>
                                                        {`${item.salutation ? (item.salutation + ' ') : ''}${item.firstName} ${item.lastName}`}
                                                    </p>
                                                </Col>

                                                <Col md="4" sm="12">
                                                    <Label>{t("Email")}: </Label>
                                                    <p>
                                                        {item.email}
                                                    </p>
                                                </Col>

                                                <Col md="4" sm="12">
                                                    <Label>{t("Department")}: </Label>
                                                    <p>
                                                        {item.department}
                                                    </p>
                                                </Col>
                                            </Row>

                                            <Row>
                                                <Col md="4" sm="12">
                                                    <Label>{t("User Role")}: </Label>
                                                    <p>
                                                        {userUtils.gerUserRole(item.role)}
                                                    </p>
                                                </Col>

                                                <Col md="4" sm="12">
                                                    <Label>{t("Phone Number")}: </Label>
                                                    {item.phoneNumber && (
                                                        <LocalizedPhoneInput
                                                            readOnly
                                                            inputClass="w-100"
                                                            country={"us"}
                                                            disableInitialCountryGuess={false}
                                                            disableCountryGuess={false}
                                                            value={item.phoneNumber}
                                                            className="background-transparent"
                                                        />
                                                    )}
                                                </Col>
                                            </Row>
                                        </li>
                                    );
                                })}
                            </ol>
                        </div>
                    </div>
                ) : null}
            </div>

            <Row className="mb-4">
                <FormGroup
                    style={{
                        display: "flex",
                        flexFlow: "row wrap",
                        justifyContent: "space-between",
                    }}
                    className="actions-buttons-container"
                >
                    <Button color="primary" disabled={isLoading} onClick={handleBack} outline>
                        {t("Back")}
                    </Button>
                    <Button color="primary" disabled={isLoading} onClick={onSubmit}>
                        {isLoading ? (
                            <>
                                {t("Creating...")}
                            </>
                        ) : (
                            <>
                                {t("Create")}
                            </>
                        )}
                    </Button>
                </FormGroup>
            </Row>

            <Modal isOpen={isLoading}>  
                <ModalHeader>
                    <div>
                        <Spinner className="me-2" animation="border" variant="info" size="sm"/>
                        {t('Creating new own company')}
                    </div>
                </ModalHeader>
                
                <ModalBody>
                    <p>
                        {t("This might take several minutes. please don't close or refresh the page")}
                    </p>
                </ModalBody>
            </Modal>
        </>
    )
}

const ReviewReport = ({
  thirdPartyType,
  ...elseProps
}) => {
    if(thirdPartyType === 'own'){
        return (
            <OwnCompany 
                {...elseProps}
            />
        );
    }

    return (
        <ThirdParty 
            {...elseProps}
            thirdPartyType={thirdPartyType}
        />
    );
};

export default withNamespaces()(ReviewReport);
